import { z } from "zod";

/** Vragenlijst uit het prototype (`TYPES` en `Q`). Labels en hulpteksten ongewijzigd. */
export const TYPES = [
  { k: "bouwkundig", l: "Bouwkundig", h: "gevel, dak, muren, kozijnen" },
  { k: "w", l: "Installatie W", h: "verwarming, koeling, ventilatie" },
  { k: "e", l: "Installatie E", h: "verlichting, groepen, data" },
  { k: "sanitair", l: "Sanitair & water", h: "lekkage, afvoer, kranen" },
  { k: "brand", l: "Brandveiligheid", h: "deuren, blussers, vluchtwegen" },
  { k: "toegang", l: "Toegang & beveiliging", h: "sloten, pasjes, hekwerk" },
  { k: "afwerking", l: "Afwerking", h: "schilderwerk, plafond, vloer" },
  { k: "buiten", l: "Terrein", h: "bestrating, groen, verlichting buiten" },
  { k: "overig", l: "Overig", h: "" },
] as const;

type Opt = { k: string; l: string; h?: string; s?: number };

export const Q = {
  danger: {
    q: "Is er gevaar voor personen?",
    opts: [
      { k: "nee", l: "Nee", s: 0 },
      { k: "mogelijk", l: "Mogelijk", h: "bij verkeerd gebruik", s: 2 },
      { k: "ja", l: "Ja, direct", h: "afzetten / melden", s: 4 },
    ],
  },
  impact: {
    q: "Wat merkt de gebruiker ervan?",
    opts: [
      { k: "geen", l: "Niets", h: "alleen zichtbaar", s: 0 },
      { k: "hinder", l: "Hinder", s: 1 },
      { k: "deel", l: "Ruimte deels onbruikbaar", s: 2 },
      { k: "stil", l: "Gebouw of proces ligt stil", s: 3 },
    ],
  },
  worsen: {
    q: "Wordt het erger als je wacht?",
    opts: [
      { k: "nee", l: "Nee", s: 0 },
      { k: "langzaam", l: "Langzaam", s: 1 },
      { k: "snel", l: "Snel, gevolgschade", s: 2 },
    ],
  },
  size: {
    q: "Hoe groot is de klus?",
    opts: [
      { k: "klein", l: "Klein", h: "< 1 uur" },
      { k: "middel", l: "Middel", h: "dagdeel" },
      { k: "groot", l: "Groot", h: "meerdere dagen, materiaal" },
    ],
  },
  solver: {
    q: "Wie lost het op?",
    opts: [
      { k: "kluswacht", l: "Kluswacht", h: "eigen dienst" },
      { k: "specialist", l: "Specialist", h: "onderaannemer" },
      { k: "aannemer", l: "Aannemer", h: "garantie / restpunt" },
      { k: "klant", l: "Klant / huurder", h: "eigen verantwoordelijkheid" },
    ],
  },
  origin: {
    q: "Hoe is het gevonden?",
    opts: [
      { k: "ronde", l: "Controleronde" },
      { k: "restpunt", l: "Restpunt project" },
      { k: "melding", l: "Melding gebruiker" },
      { k: "storing", l: "Storing / alarm" },
    ],
  },
} as const satisfies Record<string, { q: string; opts: readonly Opt[] }>;

export type QuestionKey = keyof typeof Q;
type Keys<T extends QuestionKey> = (typeof Q)[T]["opts"][number]["k"];
const keysOf = <T extends QuestionKey>(q: T) => Q[q].opts.map((o) => o.k) as unknown as [Keys<T>, ...Keys<T>[]];

export const answersSchema = z.object({
  type: z.enum(TYPES.map((t) => t.k) as unknown as [(typeof TYPES)[number]["k"]]),
  danger: z.enum(keysOf("danger")),
  impact: z.enum(keysOf("impact")),
  worsen: z.enum(keysOf("worsen")),
  size: z.enum(keysOf("size")).default("klein"),
  solver: z.enum(keysOf("solver")).default("kluswacht"),
  origin: z.enum(keysOf("origin")).default("ronde"),
});
export type Answers = z.infer<typeof answersSchema>;

export const typeLabel = (k: string): string => TYPES.find((t) => t.k === k)?.l ?? "—";
export const optLabel = (q: QuestionKey, k: string): string =>
  (Q[q].opts as readonly Opt[]).find((o) => o.k === k)?.l ?? "—";
